import { ymCounter, ymEnabled, productKind } from '@/config/env';
import { getKioskId } from '@/utils/sentry';
import { useTerminalStore, useCartStore } from '@/stores';

let initialized = false;

interface MetricaContext {
    product: string
    terminal_id: string | null
    shop_id: string | null
    cafe_slug: string | null
    kiosk_id: string | null
}

interface YmWithBuffer {
    (...args: unknown[]): void
    a?: unknown[]
    l?: number
}

// Контекст сессии: ресторан (shop_id/cafe_slug) + kiosk_id + терминал + приложение.
// Шлём и в init (параметры визита для Вебвизора/отчётов), и с каждым reachGoal
// (чтобы воронку можно было разрезать по ресторану/kiosk_id).
function buildContext(): MetricaContext {
    const terminal = useTerminalStore().terminalData;
    return {
        product: productKind,
        terminal_id: terminal?.terminal_id || null,
        shop_id: terminal?.shop_id || null,
        cafe_slug: terminal?.cafe_slug || null,
        kiosk_id: getKioskId()
    };
}

// Определяем window.ym синхронно (канонический сниппет Метрики):
// функция-очередь ставит вызовы в буфер, а tag.js (грузится асинхронно)
// потом их воспроизведёт. Без этого window.ym не существует в момент
// вызова init на свежем заходе -> init падал и события не уходили.
function ensureYm(): void {
    if (typeof window === 'undefined') return;

    const m = window;
    const ym = m.ym as YmWithBuffer | undefined;
    if (!ym) {
        m.ym = function (...args: unknown[]) {
            const buffer = m.ym as YmWithBuffer;
            buffer.a = buffer.a || [];
            buffer.a.push(args);
        } as YmWithBuffer;
        (m.ym as YmWithBuffer).l = Date.now();
    }

    if (document.getElementById('ym-tag-script')) return;

    const k = document.createElement('script');
    k.id = 'ym-tag-script';
    k.async = true;
    k.src = 'https://mc.yandex.ru/metrika/tag.js';
    const first = document.getElementsByTagName('script')[0];
    if (first && first.parentNode) {
        first.parentNode.insertBefore(k, first);
    } else {
        document.head.appendChild(k);
    }
}

export function initMetrica(): void {
    subscribeToResetState();

    if (initialized || !ymEnabled) return;
    initialized = true;

    ensureYm();

    window.ym?.(ymCounter, 'init', {
        clickmap: false,
        webvisor: true,
        trackLinks: true,
        accurateTrackBounce: true,
        params: buildContext()
    });

    // Дублируем как параметры визита отдельным вызовом — подстраховка,
    // если terminalData проинициализируется позже init.
    setVisitParams();
}

function setVisitParams(): void {
    if (!ymEnabled || typeof window === 'undefined' || typeof window.ym !== 'function') return;
    try {
        window.ym(ymCounter, 'params', buildContext());
    } catch { /* fire-and-forget */ }
}

export function ymReach(goal: string, extra: Record<string, unknown> = {}): void {
    if (!ymEnabled || typeof window === 'undefined' || typeof window.ym !== 'function') {
        return;
    }

    try {
        window.ym(ymCounter, 'reachGoal', goal, { ...buildContext(), ...extra });
    } catch {
        // fire-and-forget: Метрика не должна падать никак на UX
    }
}

// В корзине ли ещё комбо, собранное через наш флоу (выбор товара + комбо-модалка)?
// Нужно чтобы цель order_paid считалась только для реально оплаченных комбо:
// если комбо убрали из корзины до оплаты — цель не должна засчитываться.
export function comboFlowInCart(): boolean {
    try {
        const cart = useCartStore().cart || [];
        return cart.some(item => !!item && !!item.is_combo && !!item.fromComboFlow);
    } catch {
        return false;
    }
}

// Флаг «пользователь явно выбрал единичный товар в оффере комбо».
// Нужен, чтобы на оплате отдельно засчитать single_product_purchase
// (независимо от итоговой корзины). Сбрасывается между гостями (resetState).
let offerSingleChosen = false;

export function markOfferSingleChosen(): void {
    offerSingleChosen = true;
}

export function isOfferSingleChosen(): boolean {
    return offerSingleChosen;
}

let resetSubscribed = false;

function subscribeToResetState(): void {
    if (resetSubscribed) return;
    resetSubscribed = true;
    useTerminalStore().$onAction(({ name }) => {
        if (name === 'resetState') {
            offerSingleChosen = false;
        }
    });
}